import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TractorService } from "../shared/tractor.service";
import { GetPictureService } from "../../shared/services/files/getPictures.service";
import { FileUploadService } from "../../shared/services/files/fileUpload.service";
import { ToastrService } from "ngx-toastr";
import { CanComponentDeactivate } from "../../shared/guards/confirmation/confirmation.guard";
import { MaskService } from "../../shared/services/mask/mask.service";
import { authenticationService } from "@app/login/auth.service";
import { Permissions } from "@models/permissions.enum";
import {
  TractorViewModel,
  TractorStatus,
} from "@app/shared/models/tractors/TractorViewModel.model";
import { TerminalLocationsServices } from "../../shared/services/terminals/terminals.service";
import { StatesServices } from "../../shared/services/states/states.service";
import { of as observableOf } from "rxjs";
import { DriverService } from "../../driver/shared/driver.service";

@Component({
  selector: "app-tractor-detail",
  templateUrl: "./tractor-detail.component.html",
  styleUrls: ["./tractor-detail.component.css"],
})
export class TractorDetailComponent implements OnInit, CanComponentDeactivate {
  @ViewChild("profilePictureInput", { static: false }) profilePictureInput;

  tractor: TractorViewModel = new TractorViewModel();
  isNewTractor: boolean = false;
  isEditMode: boolean = false;
  isProcessing: boolean = false;
  selectedTab: string = "profile";
  hasUnsavedChanges: boolean = false;

  // UI state
  profilePicture: any = "assets/img/truck.jpg";
  previewProfilePicture: any;
  newProfileFile: any;
  originalInfo: any;

  // Data
  terminals: Array<string> = [];
  states: { abbreviation: string; fullName: string }[];

  // Masks
  dateInputMask: any;
  docType: string = "tractor";

  // Peoplenet status
  peoplenetStatus: string = "";
  isLoadingPeoplenetStatus: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tractorService: TractorService,
    private getPictureService: GetPictureService,
    private fileUploadService: FileUploadService,
    private toastr: ToastrService,
    private maskService: MaskService,
    private authService: authenticationService,
    private terminalLocationsServices: TerminalLocationsServices,
    private statesServices: StatesServices,
    private driverService: DriverService
  ) {}

  ngOnInit() {
    this.initializeMasks();
    this.terminals = this.terminalLocationsServices.getTerminalLocations();
    this.states = this.statesServices.getTwoLettersStates("US");

    this.route.params.subscribe((params) => {
      const tractorId = params["tractorId"];

      if (tractorId === "newtractor") {
        this.initializeNewTractor();
      } else if (tractorId) {
        this.loadExistingTractor(tractorId);
      }
    });
  }

  initializeMasks() {
    this.dateInputMask = this.maskService.getInputDateOnlyMask();
  }

  initializeNewTractor() {
    this.isNewTractor = true;
    this.isEditMode = true;
    this.tractor = new TractorViewModel();
    this.tractor.isActive = true;
  }

  loadExistingTractor(tractorId: string) {
    this.isNewTractor = false;
    this.isEditMode = false;

    this.tractorService.getTractorAsync(tractorId).then((result: TractorViewModel) => {
      if (!result) {
        this.toastr.error("Tractor not found", "Error");
        return;
      }

      this.tractor = result;
      this.initDateFields(this.tractor);

      if (this.tractor.picture) {
        this.profilePicture = this.getFilePath(this.tractor.picture);
      }

      this.originalInfo = JSON.stringify(this.tractor);
      this.loadPeoplenetStatus();
    });
  }

  onDriverChanged() {
    // Called when driver changes in the child component
    this.loadPeoplenetStatus();
  }

  hasPermission(permissionName: string): boolean {
    const permission = Permissions[permissionName];
    return this.authService.hasPermission(permission);
  }

  canViewProfileTab(): boolean {
    return this.hasPermission("TractorsRead") || this.hasPermission("TractorsChange");
  }

  canEditProfileTab(): boolean {
    return this.hasPermission("TractorsChange");
  }

  canViewSpecsTab(): boolean {
    return this.hasPermission("TractorsRead") || this.hasPermission("TractorsChange");
  }

  canEditSpecsTab(): boolean {
    return this.hasPermission("TractorsChange");
  }

  canViewLicenseTab(): boolean {
    return this.hasPermission("TractorsRead") || this.hasPermission("TractorsChange");
  }

  canEditLicenseTab(): boolean {
    return this.hasPermission("TractorsChange");
  }

  canViewMaintenanceTab(): boolean {
    return this.hasPermission("TractorsRead") || this.hasPermission("TractorsChange");
  }

  canEditMaintenanceTab(): boolean {
    return this.hasPermission("TractorsChange");
  }

  canEditAnyTab(): boolean {
    return this.hasPermission("TractorsChange");
  }

  onTabClicked(tab: string) {
    this.selectedTab = tab;
  }

  onTabHasChanges(hasChanges: boolean) {
    this.hasUnsavedChanges = hasChanges;
  }

  toggleEditMode() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.originalInfo = JSON.stringify(this.tractor);
    } else {
      this.saveTractor();
    }
  }

  cancelEdit() {
    if (this.isNewTractor) {
      this.router.navigate(["/tractorlist"]);
    } else {
      this.isEditMode = false;
      this.resetChangesFlag();
      this.tractor = JSON.parse(this.originalInfo);
      this.initDateFields(this.tractor);

      if (this.tractor.picture) {
        this.profilePicture = this.getFilePath(this.tractor.picture);
      } else {
        this.profilePicture = "assets/img/truck.jpg";
      }
      this.newProfileFile = null;
      this.previewProfilePicture = null;
    }
  }

  saveTractor() {
    if (!this.validateTractor(this.tractor)) {
      return;
    }

    this.isProcessing = true;

    if (this.isNewTractor) {
      this.createNewTractor();
    } else {
      this.updateExistingTractor();
    }
  }

  createNewTractor() {
    let profilePictureObservable;

    if (this.newProfileFile) {
      profilePictureObservable = this.uploadProfilePicture();
    } else {
      profilePictureObservable = observableOf(undefined);
    }

    profilePictureObservable.subscribe((result: any) => {
      if (result) {
        this.tractor.picture = result.name;
      }

      this.tractorService.createNewTractor(this.tractor).subscribe({
        next: (tractorCreated: any) => {
          this.finishTractorCreation(tractorCreated.id);
        },
        error: (errors) => {
          this.handleErrors(errors);
          this.isProcessing = false;
        },
      });
    });
  }

  updateExistingTractor() {
    if (this.newProfileFile) {
      this.uploadProfilePicture().subscribe((url: any) => {
        this.tractor.picture = url.name;
        this.saveTractorChanges();
      });
    } else {
      this.saveTractorChanges();
    }
  }

  saveTractorChanges() {
    this.tractorService.updateTractor(this.tractor).subscribe({
      next: () => {
        this.toastr.success("Tractor updated", "Success");
        this.isProcessing = false;
        this.isEditMode = false;
        this.resetChangesFlag();
        this.originalInfo = JSON.stringify(this.tractor);
        // Reload Peoplenet status in case driver changed
        this.loadPeoplenetStatus();
      },
      error: (errors) => {
        this.handleErrors(errors);
        this.isProcessing = false;
      },
    });
  }

  finishTractorCreation(tractorId: string) {
    this.toastr.success("Tractor created", "Success");
    this.isProcessing = false;
    this.isNewTractor = false;
    this.isEditMode = false;
    this.resetChangesFlag();
    this.router.navigate([`/tractors/${tractorId}`]);
  }

  validateTractor(tractor: TractorViewModel): boolean {
    if (!tractor.unitNumber || tractor.unitNumber.trim() === "") {
      this.toastr.warning("UNIT NUMBER - Required", "Alert!");
      return false;
    }
    return true;
  }

  handleErrors(errors: any) {
    if (Array.isArray(errors)) {
      for (const error of errors) {
        this.toastr.warning(error, "Alert!", {
          closeButton: true,
          enableHtml: true,
        });
      }
    } else {
      this.toastr.error("An error occurred", "Error");
    }
  }

  // Image handling
  onProfilePictureSelected(event: any) {
    if (event.target.files && event.target.files[0]) {
      this.newProfileFile = event.target.files[0];
      this.previewProfilePicture = this.getPictureService.getPreviewImage(event.target.files[0]);
      this.profilePicture = this.previewProfilePicture;
    }
  }

  uploadProfilePicture() {
    const fname: string = this.newProfileFile.name;
    const prefix = this.tractor.unitNumber || "tractor";
    return this.fileUploadService.uploadSingleFileRename(
      [this.newProfileFile],
      this.docType,
      prefix + "_" + fname
    );
  }

  getFilePath(path: string) {
    return this.getPictureService.getPicturePath(path, this.docType);
  }

  // Date field initialization
  initDateFields(tractor: TractorViewModel) {
    tractor.dateSold = this.convertToDate(tractor.dateSold);
    tractor.datePurchased = this.convertToDate(tractor.datePurchased);
    tractor.licenseExpirationDate = this.convertToDate(tractor.licenseExpirationDate);
    tractor.nextServiceDate = this.convertToDate(tractor.nextServiceDate);
  }

  convertToDate(date: any): Date | null {
    return date ? new Date(date) : null;
  }

  // Guard
  confirm(): boolean {
    if (this.hasUnsavedChanges || (this.isEditMode && !this.isNewTractor)) {
      return window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
    } else if (this.isNewTractor) {
      return window.confirm("Leave this page without creating the tractor?");
    }
    return true;
  }

  resetChangesFlag() {
    this.hasUnsavedChanges = false;
  }

  getTractorStatusLabel(): string {
    if (this.tractor?.status === undefined || this.tractor?.status === null) {
      return "Available";
    }
    const statusOptions = [
      { value: TractorStatus.Available, label: "Available" },
      { value: TractorStatus.Shop, label: "Shop" },
      { value: TractorStatus.OOS, label: "OOS" },
    ];
    const status = statusOptions.find((opt) => opt.value === this.tractor.status);
    return status ? status.label : "Available";
  }

  getStatusPillClass(): string {
    if (this.tractor?.status === undefined || this.tractor?.status === null) {
      return "status-pill status-in-service";
    }
    if (this.tractor.status === TractorStatus.Available) {
      return "status-pill status-in-service";
    } else if (this.tractor.status === TractorStatus.Shop) {
      return "status-pill status-out-of-service";
    } else if (this.tractor.status === TractorStatus.OOS) {
      return "status-pill status-out-of-service";
    }
    return "status-pill status-in-service";
  }

  getAssignedDriverName(): string {
    if (this.tractor?.driver) {
      const name = this.tractor.driver.name || this.tractor.driver.Name || "";
      const lastName = this.tractor.driver.lastName || this.tractor.driver.LastName || "";
      const fullName = `${name} ${lastName}`.trim();
      return fullName || "N/A";
    }
    return "N/A";
  }

  getDriverStatus(): string {
    if (this.isLoadingPeoplenetStatus) {
      return "LOADING...";
    }
    return this.peoplenetStatus || "NO STATUS AVAILABLE";
  }

  loadPeoplenetStatus() {
    // If no driver assigned, set status accordingly
    if (!this.tractor?.driverId) {
      this.peoplenetStatus = "NO DRIVER ASSIGNED";
      this.isLoadingPeoplenetStatus = false;
      return;
    }

    // If already loading, don't start another request
    if (this.isLoadingPeoplenetStatus) {
      return;
    }

    this.isLoadingPeoplenetStatus = true;
    this.driverService.getRecentDutyStatuses(String(this.tractor.driverId)).subscribe({
      next: (intervals: any[]) => {
        this.isLoadingPeoplenetStatus = false;

        if (!intervals || intervals.length === 0) {
          this.peoplenetStatus = "NO STATUS AVAILABLE";
          return;
        }

        // Intervals are sorted by effectiveUtc ascending
        // Find the most recent status: prefer active intervals, otherwise the latest effectiveUtc
        const now = new Date();

        // Find all currently active intervals (endUtc > now)
        const activeIntervals = intervals.filter((interval) => {
          const endUtc = new Date(interval.endUtc);
          return endUtc > now;
        });

        let mostRecentInterval: any = null;

        if (activeIntervals.length > 0) {
          // If there are active intervals, get the one with the latest effectiveUtc
          // Since intervals are sorted ascending, the last one has the latest effectiveUtc
          mostRecentInterval = activeIntervals[activeIntervals.length - 1];
        } else {
          // If no active intervals, get the interval with the latest effectiveUtc
          // Since intervals are sorted ascending, the last one has the latest effectiveUtc
          mostRecentInterval = intervals[intervals.length - 1];
        }

        if (mostRecentInterval) {
          this.peoplenetStatus = this.formatDutyStatus(mostRecentInterval.dutyStatus);
        } else {
          this.peoplenetStatus = "NO STATUS AVAILABLE";
        }
      },
      error: (error) => {
        this.isLoadingPeoplenetStatus = false;
        console.error("Error loading Peoplenet duty status:", error);
        this.peoplenetStatus = "NO STATUS AVAILABLE";
      },
    });
  }

  private formatDutyStatus(dutyStatus: number): string {
    switch (dutyStatus) {
      case 1:
        return "DRIVING";
      case 2:
        return "ON DUTY";
      case 3:
        return "OFF DUTY";
      case 4:
        return "SLEEPER BERTH";
      case 0:
      default:
        return "UNKNOWN";
    }
  }

  getLocationInfo(): string {
    return this.tractor?.currentLocationCity || this.tractor?.currentLocationState
      ? [this.tractor.currentLocationCity, this.tractor.currentLocationState]
          .filter(Boolean)
          .join(", ")
      : "-";
  }

  getPeopleNetLastUpdated(): string {
    const raw = this.tractor?.peopleNetLocationUpdatedUtc;
    if (!raw) return "";
    const d = typeof raw === "string" ? new Date(raw) : raw;
    if (isNaN(d.getTime())) return "";

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      return "Updated <1h ago";
    } else if (diffHours < 24) {
      return `Updated ${diffHours}h ago`;
    } else if (diffDays === 1) {
      return "Updated 1d ago";
    } else {
      return `Updated ${diffDays}d ago`;
    }
  }

  getPeopleNetLastUpdatedTooltip(): string {
    const raw = this.tractor?.peopleNetLocationUpdatedUtc;
    if (!raw) return "";
    const d = typeof raw === "string" ? new Date(raw) : raw;
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString();
  }

  isPeopleNetLocationStale(): boolean {
    const raw = this.tractor?.peopleNetLocationUpdatedUtc;
    if (!raw) return false;
    const d = typeof raw === "string" ? new Date(raw) : raw;
    if (isNaN(d.getTime())) return false;

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    return diffHours > 48;
  }

  // Note: Location data is currently from Fleetilla backend.
  // To fully switch to PeopleNet, backend updates would be needed to populate
  // currentLocationCity/State from PeopleNet instead of Fleetilla.

  getPeopleNetStatus(): string {
    return this.getDriverStatus();
  }

  deleteTractor() {
    if (confirm("Are you sure you want to delete this tractor?")) {
      this.isProcessing = true;
      const tractorId = this.tractor.id || (this.tractor as any).uuid;
      this.tractorService.delete({ id: tractorId }).subscribe({
        next: () => {
          this.toastr.success("Tractor deleted", "Success");
          this.isProcessing = false;
          this.router.navigate(["/tractorlist"]);
        },
        error: (errors) => {
          this.handleErrors(errors);
          this.isProcessing = false;
        },
      });
    }
  }
}
